import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Upload, FileText, Loader2, CheckCircle, XCircle, AlertCircle, ArrowLeft, ArrowRight, Check } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Progress } from '@/components/ui/progress'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { companiesApi } from '@/services/api'
import { cn } from '@/lib/utils'

interface ImportCompaniesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type WizardStep = 1 | 2 | 3
type ImportStatus = 'idle' | 'uploading' | 'processing' | 'completed' | 'failed'

interface ImportResult {
  id: string
  status: string
  total_rows?: number
  created_count?: number
  updated_count?: number
  skipped_count?: number
  error_count?: number
  errors?: Array<{ row: number; field?: string; message: string }>
}

const TARGET_FIELDS = [
  { value: 'skip', label: 'Skip / Don\'t import', required: false },
  { value: 'name', label: 'Name', required: true },
  { value: 'website', label: 'Website', required: false },
  { value: 'country', label: 'Country', required: false },
  { value: 'city', label: 'City', required: false },
  { value: 'address', label: 'Address', required: false },
  { value: 'phone', label: 'Phone', required: false },
  { value: 'email', label: 'Email', required: false },
  { value: 'business_type', label: 'Business Type', required: false },
  { value: 'employee_count', label: 'Employee Count', required: false },
  { value: 'established_year', label: 'Established Year', required: false },
  { value: 'contact_person', label: 'Contact Person', required: false },
  { value: 'contact_title', label: 'Contact Title', required: false },
  { value: 'vat_number', label: 'VAT Number', required: false },
] as const

type FieldValue = typeof TARGET_FIELDS[number]['value']

const CSV_COLUMN_MAPPING: Record<string, FieldValue> = {
  'name': 'name',
  'company_name': 'name',
  'company name': 'name',
  'companyname': 'name',
  'website': 'website',
  'url': 'website',
  'web': 'website',
  'site': 'website',
  'country': 'country',
  'city': 'city',
  'address': 'address',
  'street': 'address',
  'street_address': 'address',
  'street address': 'address',
  'phone': 'phone',
  'phone_number': 'phone',
  'phone number': 'phone',
  'telephone': 'phone',
  'tel': 'phone',
  'email': 'email',
  'company_email': 'email',
  'company email': 'email',
  'mail': 'email',
  'business_type': 'business_type',
  'business type': 'business_type',
  'industry': 'business_type',
  'sector': 'business_type',
  'employee_count': 'employee_count',
  'employee count': 'employee_count',
  'employees': 'employee_count',
  'staff': 'employee_count',
  'size': 'employee_count',
  'established_year': 'established_year',
  'established year': 'established_year',
  'year_founded': 'established_year',
  'year founded': 'established_year',
  'founded': 'established_year',
  'year': 'established_year',
  'contact_person': 'contact_person',
  'contact person': 'contact_person',
  'contact_name': 'contact_person',
  'contact name': 'contact_person',
  'contact': 'contact_person',
  'contact_title': 'contact_title',
  'contact title': 'contact_title',
  'contact_role': 'contact_title',
  'contact role': 'contact_title',
  'title': 'contact_title',
  'role': 'contact_title',
  'position': 'contact_title',
  'vat_number': 'vat_number',
  'vat number': 'vat_number',
  'vat': 'vat_number',
  'tax_id': 'vat_number',
  'tax id': 'vat_number',
}

function detectFieldMapping(header: string): FieldValue {
  const normalized = header.trim().toLowerCase()
  return CSV_COLUMN_MAPPING[normalized] ?? 'skip'
}

function parseCSVLine(line: string): string[] {
  const result: string[] = []
  let current = ''
  let inQuotes = false
  for (const char of line) {
    if (char === '"') {
      inQuotes = !inQuotes
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim())
      current = ''
    } else {
      current += char
    }
  }
  result.push(current.trim())
  return result
}

export function ImportCompaniesDialog({ open, onOpenChange }: ImportCompaniesDialogProps) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [step, setStep] = useState<WizardStep>(1)
  const [importName, setImportName] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [csvHeaders, setCsvHeaders] = useState<string[]>([])
  const [csvSampleData, setCsvSampleData] = useState<string[][]>([])
  const [columnMappings, setColumnMappings] = useState<Record<number, FieldValue>>({})
  const [importStatus, setImportStatus] = useState<ImportStatus>('idle')
  const [importId, setImportId] = useState<string | null>(null)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showErrors, setShowErrors] = useState(false)

  const { data: pollResult } = useQuery({
    queryKey: ['import-status', importId],
    queryFn: () => companiesApi.getImportStatus(importId!),
    enabled: !!importId && importStatus === 'processing',
    refetchInterval: 2000,
  })

  useEffect(() => {
    if (pollResult) {
      setImportResult(pollResult)
      if (pollResult.status === 'completed') {
        setImportStatus('completed')
        queryClient.invalidateQueries({ queryKey: ['companies'] })
        queryClient.invalidateQueries({ queryKey: ['stage-counts'] })
      } else if (pollResult.status === 'failed') {
        setImportStatus('failed')
      }
    }
  }, [pollResult, queryClient])

  const importMutation = useMutation({
    mutationFn: async ({ name, file, mapping }: { name: string; file: File; mapping: Record<number, FieldValue> }) => {
      const filteredMapping: Record<string, string> = {}
      for (const [idx, field] of Object.entries(mapping)) {
        if (field !== 'skip') {
          filteredMapping[idx] = field
        }
      }
      return companiesApi.importCompanies(name, file, filteredMapping)
    },
    onSuccess: (data) => {
      setImportId(data.import_id)
      setImportStatus('processing')
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to start import')
      setImportStatus('failed')
    },
  })

  const handleFileSelect = useCallback((file: File) => {
    setSelectedFile(file)
    setError(null)

    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result as string
      const lines = text.split('\n').filter(line => line.trim())
      
      if (lines.length === 0) {
        setError('CSV file is empty')
        return
      }

      const headers = parseCSVLine(lines[0])
      setCsvHeaders(headers)

      const sampleRows = lines.slice(1, 4).map(parseCSVLine)
      setCsvSampleData(sampleRows)

      const initialMappings: Record<number, FieldValue> = {}
      headers.forEach((header, idx) => {
        initialMappings[idx] = detectFieldMapping(header)
      })
      setColumnMappings(initialMappings)
    }
    reader.readAsText(file)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file && file.name.toLowerCase().endsWith('.csv')) {
      handleFileSelect(file)
    } else {
      setError('Please drop a CSV file')
    }
  }, [handleFileSelect])

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }, [handleFileSelect])

  const handleMappingChange = useCallback((columnIndex: number, value: FieldValue) => {
    setColumnMappings(prev => ({ ...prev, [columnIndex]: value }))
  }, [])

  const usedFields = useMemo(() => {
    const used = new Set<FieldValue>()
    for (const field of Object.values(columnMappings)) {
      if (field !== 'skip') {
        used.add(field)
      }
    }
    return used
  }, [columnMappings])

  const isNameMapped = useMemo(() => {
    return Object.values(columnMappings).includes('name')
  }, [columnMappings])

  const handleNextStep = useCallback(() => {
    if (step === 1) {
      if (!importName.trim()) {
        setError('Please enter a name for this import')
        return
      }
      if (!selectedFile) {
        setError('Please select a CSV file')
        return
      }
      setError(null)
      setStep(2)
    }
  }, [step, importName, selectedFile])

  const handlePrevStep = useCallback(() => {
    if (step === 2) {
      setStep(1)
    }
  }, [step])

  const handleStartImport = useCallback(() => {
    if (!isNameMapped) {
      setError('Company name is required. Please map a column to "Name".')
      return
    }
    if (!selectedFile) {
      return
    }

    setError(null)
    setStep(3)
    setImportStatus('uploading')
    importMutation.mutate({ 
      name: importName.trim(), 
      file: selectedFile,
      mapping: columnMappings 
    })
  }, [isNameMapped, selectedFile, importName, columnMappings, importMutation])

  const handleClose = useCallback((force = false) => {
    if ((importStatus === 'uploading' || importStatus === 'processing') && !force) {
      return
    }
    setStep(1)
    setImportName('')
    setSelectedFile(null)
    setCsvHeaders([])
    setCsvSampleData([])
    setColumnMappings({})
    setImportStatus('idle')
    setImportId(null)
    setImportResult(null)
    setError(null)
    setShowErrors(false)
    onOpenChange(false)
  }, [importStatus, onOpenChange])

  const handleForceClose = useCallback(() => {
    handleClose(true)
  }, [handleClose])

  const canProceedStep1 = importName.trim() && selectedFile && csvHeaders.length > 0

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Import Companies</DialogTitle>
          <DialogDescription>
            {step === 1 && 'Upload a CSV file to import companies into your pipeline.'}
            {step === 2 && 'Map CSV columns to company fields.'}
            {step === 3 && 'Processing your import...'}
          </DialogDescription>
          <div className="flex items-center gap-2 pt-2">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={cn(
                  'flex items-center gap-2',
                  s < step && 'text-green-600',
                  s === step && 'text-primary font-medium',
                  s > step && 'text-muted-foreground'
                )}
              >
                <div
                  className={cn(
                    'w-6 h-6 rounded-full flex items-center justify-center text-xs border',
                    s < step && 'bg-green-600 text-white border-green-600',
                    s === step && 'border-primary text-primary',
                    s > step && 'border-muted-foreground'
                  )}
                >
                  {s < step ? <Check className="h-3 w-3" /> : s}
                </div>
                <span className="text-sm">
                  {s === 1 && 'Upload'}
                  {s === 2 && 'Map Columns'}
                  {s === 3 && 'Import'}
                </span>
                {s < 3 && <ArrowRight className="h-4 w-4 text-muted-foreground mx-1" />}
              </div>
            ))}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto py-4">
          {step === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="import-name">Import Name (This will be used as the Data Source)</Label>
                <Input
                  id="import-name"
                  placeholder="e.g., Athens Exhibition 2026, LinkedIn Export"
                  value={importName}
                  onChange={(e) => setImportName(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Give this import a name to help track where your companies came from.
                </p>
              </div>

              <div className="space-y-2">
                <Label>CSV File</Label>
                <div
                  className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={handleFileInputChange}
                  />
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileText className="h-8 w-8 text-primary" />
                      <div className="text-left">
                        <p className="font-medium">{selectedFile.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {(selectedFile.size / 1024).toFixed(1)} KB · {csvHeaders.length} columns
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Drag and drop a CSV file, or click to browse
                      </p>
                    </>
                  )}
                </div>
              </div>

              {csvSampleData.length > 0 && (
                <div className="space-y-2">
                  <Label>Preview (first 3 rows)</Label>
                  <div className="border rounded-lg overflow-auto max-h-40">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          {csvHeaders.map((header, i) => (
                            <th key={i} className="px-3 py-2 text-left font-medium whitespace-nowrap">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvSampleData.map((row, rowIdx) => (
                          <tr key={rowIdx} className="border-t">
                            {row.map((cell, cellIdx) => (
                              <td key={cellIdx} className="px-3 py-2 truncate max-w-[150px]">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              {!isNameMapped && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Company name is required. Please map a column to "Name".
                  </AlertDescription>
                </Alert>
              )}

              <div className="border rounded-lg overflow-auto max-h-[400px]">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium w-1/4">CSV Column</th>
                      <th className="px-3 py-2 text-left font-medium w-1/3">Sample Data</th>
                      <th className="px-3 py-2 text-left font-medium w-1/3">Maps To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvHeaders.map((header, idx) => {
                      const currentMapping = columnMappings[idx] ?? 'skip'
                      const isAutoDetected = detectFieldMapping(header) !== 'skip' && currentMapping === detectFieldMapping(header)
                      const sampleValue = csvSampleData[0]?.[idx] ?? ''
                      
                      return (
                        <tr 
                          key={idx} 
                          className={cn(
                            'border-t',
                            isAutoDetected && currentMapping !== 'skip' && 'bg-green-50 dark:bg-green-950/30',
                            !isAutoDetected && currentMapping === 'skip' && 'bg-orange-50 dark:bg-orange-950/30'
                          )}
                        >
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              {isAutoDetected && currentMapping !== 'skip' && (
                                <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
                              )}
                              {!isAutoDetected && currentMapping === 'skip' && (
                                <AlertCircle className="h-4 w-4 text-orange-500 shrink-0" />
                              )}
                              <span className="font-medium truncate">{header}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground truncate max-w-[200px]">
                            {sampleValue}
                          </td>
                          <td className="px-3 py-2">
                            <Select
                              value={currentMapping}
                              onValueChange={(value) => handleMappingChange(idx, value as FieldValue)}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {TARGET_FIELDS.map((field) => {
                                  const isUsed = usedFields.has(field.value) && field.value !== currentMapping && field.value !== 'skip'
                                  return (
                                    <SelectItem 
                                      key={field.value} 
                                      value={field.value}
                                      disabled={isUsed}
                                    >
                                      <span className={cn(isUsed && 'text-muted-foreground')}>
                                        {field.label}
                                        {field.required && ' *'}
                                        {isUsed && ' (already mapped)'}
                                      </span>
                                    </SelectItem>
                                  )
                                })}
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <p className="text-xs text-muted-foreground">
                * Required field. Green rows are auto-detected. Orange rows need your attention.
              </p>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {step === 3 && (
            <>
              {(importStatus === 'uploading' || importStatus === 'processing') && (
                <div className="py-8 text-center space-y-4">
                  <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
                  <div>
                    <p className="font-medium">
                      {importStatus === 'uploading' ? 'Uploading...' : 'Processing import...'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {importStatus === 'processing' && importResult
                        ? `${(importResult.created_count || 0) + (importResult.updated_count || 0)} companies processed`
                        : 'This may take a moment'}
                    </p>
                  </div>
                  {importStatus === 'processing' && importResult && importResult.total_rows && (
                    <Progress
                      value={
                        ((importResult.created_count || 0) +
                          (importResult.updated_count || 0) +
                          (importResult.skipped_count || 0) +
                          (importResult.error_count || 0)) /
                        importResult.total_rows *
                        100
                      }
                    />
                  )}
                </div>
              )}

              {importStatus === 'completed' && importResult && (
                <div className="py-4 space-y-4">
                  <div className="text-center">
                    <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-2" />
                    <p className="font-medium text-lg">Import Complete</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-green-50 dark:bg-green-950 rounded-lg p-4">
                      <p className="text-2xl font-bold text-green-600">{importResult.created_count || 0}</p>
                      <p className="text-sm text-muted-foreground">Companies Created</p>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-950 rounded-lg p-4">
                      <p className="text-2xl font-bold text-blue-600">{importResult.updated_count || 0}</p>
                      <p className="text-sm text-muted-foreground">Companies Updated</p>
                    </div>
                  </div>
                  {(importResult.error_count || 0) > 0 && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        {importResult.error_count} rows had errors.{' '}
                        <button
                          className="underline"
                          onClick={() => setShowErrors(!showErrors)}
                        >
                          {showErrors ? 'Hide' : 'Show'} details
                        </button>
                      </AlertDescription>
                    </Alert>
                  )}
                  {showErrors && importResult.errors && importResult.errors.length > 0 && (
                    <div className="border rounded-lg overflow-auto max-h-40 text-sm">
                      <table className="w-full">
                        <thead className="bg-muted">
                          <tr>
                            <th className="px-3 py-2 text-left">Row</th>
                            <th className="px-3 py-2 text-left">Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importResult.errors.slice(0, 20).map((err, i) => (
                            <tr key={i} className="border-t">
                              <td className="px-3 py-2">{err.row}</td>
                              <td className="px-3 py-2">{err.message}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {importStatus === 'failed' && (
                <div className="py-8 text-center space-y-4">
                  <XCircle className="h-12 w-12 mx-auto text-destructive" />
                  <div>
                    <p className="font-medium">Import Failed</p>
                    <p className="text-sm text-muted-foreground">
                      {error || importResult?.errors?.[0]?.message || 'An error occurred'}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          {step === 1 && (
            <>
              <Button variant="outline" onClick={() => handleClose()}>
                Cancel
              </Button>
              <Button onClick={handleNextStep} disabled={!canProceedStep1}>
                Next
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={handlePrevStep}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button onClick={handleStartImport} disabled={!isNameMapped}>
                <Upload className="mr-2 h-4 w-4" />
                Start Import
              </Button>
            </>
          )}
          {step === 3 && (importStatus === 'uploading' || importStatus === 'processing') && (
            <Button variant="outline" onClick={handleForceClose}>
              Close (Import continues in background)
            </Button>
          )}
          {step === 3 && (importStatus === 'completed' || importStatus === 'failed') && (
            <Button onClick={() => handleClose()}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
